import type { AxiosHeaders } from 'axios';
import { createQueryParams } from '@/utils/query-params';
import { api } from './index';

const BASE_PATH = '/api/internal/shift_change_requests';

// Mirrors ShiftChangeRequest.reason_codes.
export type ShiftChangeReasonCode =
  | 'representative_request'
  | 'caregiver_personal'
  | 'caregiver_health'
  | 'route_change'
  | 'incident'
  | 'other';

export type ShiftChangeRequest = {
  id: number;
  shiftId: number;
  requestType: 'time_change' | 'caregiver_change';
  humanRequestType: string;
  aasmState: 'pending' | 'approved' | 'rejected' | 'expired' | 'invalidated';
  humanAasmState: string;
  // The comment; on legacy requests (reasonCode null) the whole free-text reason.
  reason: string | null;
  reasonCode: ShiftChangeReasonCode | null;
  humanReason: string | null;
  patientFullName: string;
  caregiverFullName: string;
  caregiverHumanCareer: string | null;
  requestedCaregiverFullName: string | null;
  humanShiftDate: string;
  humanCurrentTimes: string | null;
  humanRequestedTimes: string | null;
  humanCreatedAt: string;
  humanProcessedAt: string | null;
  createdAt: string;
  currentStartDatetime: string;
  processedByEmail: string | null;
};

// Group and career narrowing happen server-side (permission-aware); free text stays
// in the browser like every other list. Any query other than `aasmState: 'pending'`
// must send both dates, at most 93 days apart (the server answers 400 otherwise).
export type ShiftChangeRequestFilters = {
  aasmState?: string;
  aasmStateIn?: ShiftChangeRequest['aasmState'][];
  reasonCodeIn?: (ShiftChangeReasonCode | 'without_reason')[];
  requestTypeIn?: ShiftChangeRequest['requestType'][];
  createdFrom?: string;
  createdTo?: string;
  groupIdIn?: number[];
  caregiverCareerIn?: string[];
};

export const shiftChangeRequestsApi = {
  index(filters: ShiftChangeRequestFilters = {}): Promise<ShiftChangeRequest[]> {
    const query = createQueryParams(filters);
    const url = query ? `${BASE_PATH}?${query}` : BASE_PATH;

    return api({ method: 'get', url }).then(response => response.data.shiftChangeRequests);
  },
  download(filters: ShiftChangeRequestFilters): Promise<{ headers: AxiosHeaders; data: Blob }> {
    return api({
      method: 'get',
      url: `${BASE_PATH}.xlsx?${createQueryParams(filters)}`,
      responseType: 'blob',
    });
  },
  update(id: number, actionType: 'approve' | 'reject'): Promise<ShiftChangeRequest> {
    return api({
      method: 'patch',
      url: `${BASE_PATH}/${id}`,
      data: { actionType },
    }).then(response => response.data.shiftChangeRequest);
  },
};
